// Client-side invoice filter model + predicate (used by the FilterBar component
// and every queue screen). Kept out of the component file so the component
// module exports only a component (react-refresh).
import type { Invoice } from './types'
import type { InvoiceListParams } from '../api/types'
import { toNumber } from './format'

/** One company or vendor picked in the Filters dialog: the id the query uses
    and the name the dialog shows. */
export interface FilterPick {
  id: string
  name: string
}

export interface InvoiceFilter {
  /** The picked companies and vendors, ANY of which keeps a row (owner,
      2026-10-14: every filter dropdown is multi-select). The API filters on the
      id — authority and visibility key on the id everywhere, and a company may
      be renamed — while the name is what the dialog shows. An empty list does
      not narrow. */
  companies: FilterPick[]
  vendors: FilterPick[]
  minTotal: string
  maxTotal: string
  fromDate: string
  toDate: string
}

export const emptyFilter: InvoiceFilter = {
  companies: [],
  vendors: [],
  minTotal: '',
  maxTotal: '',
  fromDate: '',
  toDate: '',
}

export function isFilterActive(f: InvoiceFilter): boolean {
  return Object.values(f).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ''))
}

/** A stored amount is PRESENT (vs. absent) — distinguishes a genuine 0 from
    null/undefined/empty so the total filter treats "no amount yet" as unknown
    (passes) rather than $0. */
function hasValue(v: unknown): boolean {
  return v !== null && v !== undefined && v !== ''
}

/** Sorted, de-duplicated, non-empty option list — feeds the Filter button's
    company/vendor (provider) dropdowns from the rows behind the timeline badges. */
export function distinct(values: (string | undefined)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b))
}

/**
 * THE COMPANY FILTER'S OPTIONS, by ID (code review D-14, 2026-10-03): one per
 * company that has a row behind the badges, named from the company master.
 * Built by name before, taking the id of whichever row came first, so a
 * released row's partition id could stand in for every Import Control row of
 * that company and filter them all away. A row with no company offers nothing.
 */
export function companyFilterOptions(
  rows: readonly Pick<Invoice, 'company_id' | 'company_name'>[],
  masterNames: ReadonlyMap<string, string>,
): { id: string; name: string }[] {
  const byId = new Map<string, string>()
  for (const r of rows) {
    const id = (r.company_id ?? '').trim()
    if (id === '' || byId.has(id)) continue
    byId.set(id, masterNames.get(id) ?? r.company_name ?? id)
  }
  return [...byId.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
}

/** A pick's key: its id, or its name where a row not yet matched to the vendor
    master has no id. The Filters dialog keys its choices the same way. */
export function filterPickKey(pick: FilterPick): string {
  return pick.id || pick.name
}

/**
 * THE VENDOR FILTER'S OPTIONS, one per vendor id (code review D-03, 2026-10-17),
 * and one per name among rows with no vendor id. Built by name before, so two
 * vendors with the same name and different ids became one option carrying the
 * first row's id, and picking it filtered the other vendor's rows away.
 */
export function vendorFilterOptions(rows: readonly Pick<Invoice, 'vendor_id' | 'vendor_name'>[]): FilterPick[] {
  const byKey = new Map<string, FilterPick>()
  for (const r of rows) {
    const name = r.vendor_name ?? ''
    if (name === '') continue
    const pick = { id: r.vendor_id ?? '', name }
    const key = filterPickKey(pick)
    if (!byKey.has(key)) byKey.set(key, pick)
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** Text equality the way Dataverse `eq` does it — ignoring case. The client
    predicates below use this so a queue and an Archives lookup can never
    disagree about whether a row matches the company/vendor picked in the
    filter. */
function sameText(stored: string | null | undefined, wanted: string): boolean {
  return (stored ?? '').toLowerCase() === wanted.toLowerCase()
}

/** Translate the shared filter into the API's named query parameters, for the
    screens where the set is unbounded and paging must compose with the filter
    (Archives). The queue screens filter their loaded page in memory instead
    (`applyFilters`), and the two must agree — which is why the null-visibility
    rule is stated on both sides: a record with no total and no date stays
    VISIBLE under a range filter, because at Import Control those are exactly the
    rows still needing entry. The API keeps them unless `require_date` is set.

    Note what is no longer here: the client used to build an OData `$filter`
    string. That was coupling and an injection surface at once; every parameter
    below is named, typed and validated server-side. */
export function filterToParams(f: InvoiceFilter): Partial<InvoiceListParams> {
  const params: Partial<InvoiceListParams> = {}
  // Company and vendor are exact choices from the dialog, sent as ids. A
  // company id is intersected with the caller's visible set server-side and can
  // never widen it.
  const companyIds = f.companies.map((c) => c.id).filter((id) => id !== '')
  const vendorIds = f.vendors.map((v) => v.id).filter((id) => id !== '')
  // A vendor pick with no id (a row not yet matched to the vendor master) goes
  // by name, as `applyFilters` matches it (code review D-03, 2026-10-17). It
  // used to be dropped, so Archives showed every vendor under the filter.
  const vendorNames = f.vendors.filter((v) => v.id === '').map((v) => v.name)
  if (companyIds.length > 0) params.company_id = companyIds
  if (vendorIds.length > 0) params.vendor_id = vendorIds
  if (vendorNames.length > 0) params.vendor_name = vendorNames
  if (f.minTotal !== '') {
    const n = toNumber(f.minTotal)
    if (Number.isFinite(n)) params.total_min = n
  }
  if (f.maxTotal !== '') {
    const n = toNumber(f.maxTotal)
    if (Number.isFinite(n)) params.total_max = n
  }
  if (f.fromDate) params.date_from = f.fromDate
  if (f.toDate) params.date_to = f.toDate
  return params
}

export function applyFilters(invoices: Invoice[], f: InvoiceFilter): Invoice[] {
  return invoices.filter((inv) => {
    // Case-insensitive, like Dataverse `eq` — the Archives screen pushes the
    // same company/vendor choice into the query.
    // THE COMPANY BY ID, always: the invoice's own `company_id`, the same
    // field every screen reads, before and after release (code review D-14).
    // The dialog always supplies the id (`companyFilterOptions`). A row
    // passes when it matches ANY pick.
    if (f.companies.length > 0 && !f.companies.some((c) => (inv.company_id ?? '') === c.id)) return false
    // A vendor pick with no id (a row not yet matched to the vendor master)
    // matches on the name instead.
    if (
      f.vendors.length > 0 &&
      !f.vendors.some((v) => (v.id ? (inv.vendor_id ?? '') === v.id : sameText(inv.vendor_name, v.name)))
    )
      return false
    // Unknown values pass: a record with no total/date must stay visible (at
    // Import Control these are often the thing still to be entered).
    if (hasValue(inv.total)) {
      const total = toNumber(inv.total)
      if (f.minTotal !== '' && total < toNumber(f.minTotal)) return false
      if (f.maxTotal !== '' && total > toNumber(f.maxTotal)) return false
    }
    if (f.fromDate || f.toDate) {
      const d = (inv.invoice_date ?? '').slice(0, 10)
      if (d) {
        if (f.fromDate && d < f.fromDate) return false
        if (f.toDate && d > f.toDate) return false
      }
    }
    return true
  })
}
